import React, { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import {
  listWorkUnitsApi,
  createWorkUnitApi,
  patchWorkUnitApi,
  deleteWorkUnitApi,
  type WorkUnit,
  type WorkUnitKind,
} from '@/services/productionOrders'
import { formatDocumentError } from '@/utils/documentErrorMessages'

export interface WorkUnitsModalProps {
  isOpen: boolean
  onClose: () => void
  onUpdated?: () => void
}

const TAB_CONFIG: Array<{ kind: WorkUnitKind; label: string }> = [
  { kind: 'wood', label: 'Đơn vị mộc' },
  { kind: 'paint', label: 'Đơn vị sơn' },
  { kind: 'subcontract', label: 'Gia công ngoài' },
]

export function WorkUnitsModal({
  isOpen,
  onClose,
  onUpdated,
}: WorkUnitsModalProps): React.ReactElement | null {
  const [activeTab, setActiveTab] = useState<WorkUnitKind>('wood')
  const [units, setUnits] = useState<WorkUnit[]>([])
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Form state
  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const loadUnits = async () => {
    setLoading(true)
    setErrorMsg(null)
    try {
      const data = await listWorkUnitsApi()
      setUnits(data)
    } catch (err: any) {
      setErrorMsg(formatDocumentError(err).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      void loadUnits()
      setIsAdding(false)
      setEditingId(null)
    }
  }, [isOpen])

  if (!isOpen) return null

  const filteredUnits = units.filter((u) => u.kind === activeTab)

  const resetForm = () => {
    setIsAdding(false)
    setEditingId(null)
    setCode('')
    setName('')
    setNote('')
    setErrorMsg(null)
  }

  const handleStartEdit = (u: WorkUnit) => {
    setEditingId(u.id)
    setIsAdding(false)
    setCode(u.code)
    setName(u.name)
    setNote(u.note || '')
    setErrorMsg(null)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setErrorMsg('Vui lòng nhập tên đơn vị sản xuất')
      return
    }

    setSubmitting(true)
    setErrorMsg(null)
    try {
      if (editingId) {
        await patchWorkUnitApi(editingId, {
          name: name.trim(),
          note: note.trim() || undefined,
        })
      } else {
        if (!code.trim()) {
          setErrorMsg('Vui lòng nhập mã đơn vị sản xuất')
          setSubmitting(false)
          return
        }
        await createWorkUnitApi({
          code: code.trim().toUpperCase(),
          name: name.trim(),
          kind: activeTab,
          note: note.trim() || undefined,
        })
      }
      resetForm()
      await loadUnits()
      onUpdated?.()
    } catch (err: any) {
      setErrorMsg(formatDocumentError(err).message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleStatus = async (u: WorkUnit) => {
    const newStatus = u.status === 'active' ? 'inactive' : 'active'
    setErrorMsg(null)
    try {
      await patchWorkUnitApi(u.id, { status: newStatus })
      await loadUnits()
      onUpdated?.()
    } catch (err: any) {
      setErrorMsg(formatDocumentError(err).message)
    }
  }

  const handleDelete = async (u: WorkUnit) => {
    if (!window.confirm(`Bạn có chắc chắn muốn xóa đơn vị "${u.code} - ${u.name}"?`)) return
    setErrorMsg(null)
    try {
      await deleteWorkUnitApi(u.id)
      await loadUnits()
      onUpdated?.()
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      if (err?.code === 'IN_USE') {
        setErrorMsg(
          `Đơn vị "${u.code}" đang được gán cho dòng kế hoạch sản xuất nên không thể xóa. Bạn có thể chuyển sang "Ngừng dùng".`,
        )
      } else {
        setErrorMsg(formatted.message)
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-bold text-slate-800">Quản lý Đơn vị Sản xuất</h2>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 text-xl font-bold"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-6 pt-3">
          {TAB_CONFIG.map((t) => (
            <button
              key={t.kind}
              type="button"
              className={`border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
                activeTab === t.kind
                  ? 'border-emerald-700 text-emerald-800 bg-white rounded-t'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
              onClick={() => {
                setActiveTab(t.kind)
                resetForm()
              }}
            >
              {t.label} ({units.filter((u) => u.kind === t.kind).length})
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {errorMsg && (
            <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              {errorMsg}
            </div>
          )}

          {/* Add / Edit Form */}
          {isAdding || editingId ? (
            <form onSubmit={handleSave} className="rounded-lg border border-slate-300 bg-slate-50 p-4 space-y-3">
              <h3 className="font-bold text-slate-800 text-sm">
                {editingId ? 'Chỉnh sửa đơn vị sản xuất' : `Thêm mới ${TAB_CONFIG.find((t) => t.kind === activeTab)?.label}`}
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Mã đơn vị <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    disabled={Boolean(editingId)}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="VD: MOC-01, SON-A..."
                    className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm font-mono uppercase focus:border-emerald-600 outline-none disabled:bg-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Tên đơn vị <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="VD: Xưởng Mộc 1, Dây chuyền Sơn A..."
                    className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-emerald-600 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Ghi chú</label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Ghi chú thêm về đơn vị..."
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-emerald-600 outline-none"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button size="sm" variant="secondary" type="button" onClick={resetForm} disabled={submitting}>
                  Hủy
                </Button>
                <Button size="sm" variant="primary" type="submit" disabled={submitting}>
                  {submitting ? 'Đang lưu...' : 'Lưu đơn vị'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">
                Danh sách các đơn vị thuộc loại <strong>{TAB_CONFIG.find((t) => t.kind === activeTab)?.label}</strong>
              </span>
              <Button size="sm" variant="primary" onClick={() => setIsAdding(true)}>
                + Thêm đơn vị
              </Button>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <p className="py-8 text-center text-sm text-slate-400">Đang tải dữ liệu...</p>
          ) : filteredUnits.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500 italic">
              Chưa có đơn vị sản xuất nào thuộc loại này.
            </p>
          ) : (
            <div className="overflow-x-auto rounded border border-slate-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-100 text-xs font-bold uppercase text-slate-700">
                  <tr>
                    <th className="px-3 py-2">Mã</th>
                    <th className="px-3 py-2">Tên đơn vị</th>
                    <th className="px-3 py-2">Trạng thái</th>
                    <th className="px-3 py-2">Ghi chú</th>
                    <th className="px-3 py-2 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredUnits.map((u) => (
                    <tr key={u.id} className={u.status === 'inactive' ? 'bg-slate-50 opacity-60' : 'hover:bg-slate-50'}>
                      <td className="px-3 py-2 font-mono font-bold text-slate-800">{u.code}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{u.name}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${
                            u.status === 'active'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-slate-200 text-slate-700'
                          }`}
                        >
                          {u.status === 'active' ? 'Đang dùng' : 'Ngừng dùng'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{u.note || '—'}</td>
                      <td className="px-3 py-2 text-right space-x-1.5">
                        <button
                          type="button"
                          className="text-xs font-semibold text-emerald-700 hover:underline"
                          onClick={() => handleStartEdit(u)}
                        >
                          Sửa
                        </button>
                        <button
                          type="button"
                          className="text-xs font-semibold text-slate-600 hover:underline"
                          onClick={() => handleToggleStatus(u)}
                        >
                          {u.status === 'active' ? 'Ngừng dùng' : 'Kích hoạt'}
                        </button>
                        <button
                          type="button"
                          className="text-xs font-semibold text-rose-600 hover:underline"
                          onClick={() => handleDelete(u)}
                        >
                          Xóa
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-6 py-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  )
}
